'use client';

import { EditableContent } from '../../edit/EditableContent';
import { EditableMedia } from '../../edit/EditableMedia';
import { buildSiteImageProxyUrl, buildSiteImageSrcSet } from '../../lib/imageProxy';
import { SITE_IMAGE_SIZES } from '../../lib/responsiveImageSizes';
import { defaultPrivatePartyFields } from '../../lib/defaultFormFields';
import { parseFormFields } from '../../lib/parseFormFields';
import { pickBlockField } from '../../lib/localized';
import { pickMediaUrl } from '../../lib/media';
import { pickChrome } from '../../lib/siteChromeCopy';
import type { BlockRenderProps } from '../../types';
import { FormFieldControl } from '../forms/FormFieldControls';
import { SiteLeadForm } from '../forms/SiteLeadForm';

/** Chef’s table — photograph beside a quiet inquiry. The lead is its own node so, after a contact form, the heading can span as its own chapter. */
export function PrivatePartyInquirySalon({
  block,
  locale,
  isActive,
  activeEditTarget,
  editable,
}: BlockRenderProps) {
  const heading = pickBlockField(block.content, 'heading', locale);
  const supporting = pickBlockField(block.content, 'supporting', locale);
  const ctaLabel = pickBlockField(block.content, 'ctaLabel', locale);
  const note = pickBlockField(block.content, 'note', locale);
  const salonImage = pickMediaUrl(block, 'salonImage');
  const salonSrc = salonImage ? buildSiteImageProxyUrl(salonImage, 960, 78) : undefined;
  const salonSrcSet = salonImage
    ? buildSiteImageSrcSet(salonImage, [480, 720, 960, 1280], 78)
    : undefined;
  const target = isActive ? activeEditTarget : null;
  const operational = parseFormFields(pickBlockField(block.content, 'formFields', locale));
  const fields = operational.length > 0 ? operational : defaultPrivatePartyFields(locale);
  const formKey = block.dataBinding?.kind === 'form' ? block.dataBinding.formKey : 'private_party';

  return (
    <section
      className={['ws-party-salon', isActive ? 'ws-block--active' : ''].filter(Boolean).join(' ')}
      data-block-id={block.blockId}
      data-component={block.componentType}
      data-registry-key={block.registryBlockKey}
      id="party"
    >
      <div className="ws-party-salon__inner">
        <div className="ws-party-salon__stage">
          {salonImage ? (
            <EditableMedia
              fieldKey="salonImage"
              editable={editable}
              activeEditTarget={target}
              className="ws-party-salon__frame"
              src={salonSrc}
              srcSet={salonSrcSet}
              sizes={SITE_IMAGE_SIZES.partySalon}
              alt={heading || 'Private dining'}
              width={720}
              height={900}
              loading="lazy"
              decoding="async"
            />
          ) : (
            <div className="ws-party-salon__frame ws-party-salon__frame--empty" aria-hidden />
          )}
        </div>
        <div className="ws-party-salon__lead">
          <EditableContent
            as="h2"
            fieldKey="heading"
            editable={editable}
            activeEditTarget={target}
            className="ws-party-salon__title"
          >
            {heading}
          </EditableContent>
          <EditableContent
            fieldKey="supporting"
            editable={editable}
            activeEditTarget={target}
            className="ws-party-salon__supporting"
          >
            {supporting}
          </EditableContent>
        </div>
        <div className="ws-party-salon__card">
          <SiteLeadForm
            locale={locale}
            formKey={formKey}
            editable={editable}
            className="ws-party-salon__form"
          >
            {fields.map((field) => (
              <FormFieldControl
                key={field.name}
                field={field}
                locale={locale}
                fieldClassName="ws-form__field"
                editable={editable}
                activeEditTarget={target}
              />
            ))}
            <EditableContent
              as="button"
              type="submit"
              fieldKey="ctaLabel"
              editable={editable}
              activeEditTarget={target}
              className="ws-party-salon__submit"
            >
              {ctaLabel || pickChrome('submitFallbackReservation', locale)}
            </EditableContent>
          </SiteLeadForm>
          {note || editable ? (
            <EditableContent
              fieldKey="note"
              editable={editable}
              activeEditTarget={target}
              className="ws-party-salon__note"
            >
              {note}
            </EditableContent>
          ) : null}
        </div>
      </div>
    </section>
  );
}
